import { Location } from '@angular/common';
import { AfterViewInit, Component, inject, Inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Payment, ThemeData } from '@models/account.model';
import { dialogMessage } from '@models/dialog-communication.model';
import { Doctor } from '@models/doctor.model';
import { feedbackTypes } from '@modules/shared/dialogs/feedback-dialog/feedback-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@services/alert.service';
import { ConfigLocaleService } from '@services/configLocale.service';
import { DisplayService } from '@services/display.service';
import { DoctorService } from '@services/doctor.service';
import { FeedbackService } from '@services/feedback.service';
import { LoggingService } from '@services/logging.service';
import { PaymentService } from '@services/payment.service';
import { TagManagerService } from '@services/tag-manager.service';
import { UserService } from '@services/user.service';
import { calculateFontColour } from '@utilities/colour-utils';
import { downloadSummaryOfPayments } from '@utilities/pdf-utils';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
@Component({
  selector: 'gc-view-payment',
  templateUrl: './view-payment.component.html',
  styleUrls: ['./view-payment.component.scss'],
  standalone: false,
})
export class ViewPaymentComponent implements AfterViewInit, OnChanges {
  @Input() payment: Payment;
  @Input() url: string;
  @Input() theme: ThemeData;
  payAllInvoices: boolean;
  peachpayEmbedded = false;
  currencyCode$: Observable<string>;
  doctor$: Observable<Doctor>;
  invoices: any[] = [];

  private readonly tagManagerService = inject(TagManagerService);
  private readonly userService: UserService = inject(UserService);
  private readonly alertService: AlertService = inject(AlertService);
  userID: number = this.userService.$userID();
  constructor(
    public dialog: MatDialog,
    private readonly display: DisplayService,
    private readonly doctorService: DoctorService,
    private readonly route: ActivatedRoute,
    private readonly configService: ConfigLocaleService,
    private readonly router: Router,
    private readonly sanitizer: DomSanitizer,
    private readonly logger: LoggingService,
    private readonly feedback: FeedbackService,
    private readonly translateService: TranslateService,
    private readonly paymentService: PaymentService,
    public location: Location,
  ) {}

  ngAfterViewInit() {
    this.currencyCode$ = of(this.configService.$currencyCode());
    this.doctor$ = this.doctorService.getDoctor$(this.payment?.themeData.diary_uuid);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['payment'] && this.payment) {
      this.getPatient();
    }
  }

  onlyOnePaymentOption(payment) {
    const payments: string[] = [payment.ozowPaymentUrl, payment.peachpayPaymentUrl, payment.fnbPaymentUrl, payment.stripePaymentUrl];
    const paymentOptionsLength = payments.filter((p) => p !== null).length;
    // True if the old payment model is used or if there is only one payment option.
    if ((paymentOptionsLength === 0 && payment.paymentUrl) || paymentOptionsLength === 1) {
      return true;
    } else {
      return false;
    }
  }

  determinePayment(event: Event, payment?: Payment) {
    const onlyOnePayment = this.onlyOnePaymentOption(payment);
    if (!onlyOnePayment) {
      this.makePayment(event);
    } else if (onlyOnePayment && payment.peachpayPaymentUrl && payment.peachpayCheckoutId) {
      // This might be an old request and thus has no checkoutId
      this.peachpayEmbedded = true;
      this.payAllInvoices = true;
    } else {
      this.openPayment(payment, event);
    }
  }

  loginForStoredCards(event: Event, payment?: Payment) {
    this.alertService
      .loginDialog$()
      .pipe(
        tap((result) => {
          if (result) {
            this.userID = this.userService.$userID();
            this.determinePayment(event, payment);
          }
        }),
      )
      .subscribe();
  }

  // Open direct payment lnik if there is only one option
  openPayment(payment, event: Event) {
    this.logger.addToJourney('action/open-payment-iframe');
    const paymentUrlBuild = new URL(payment.paymentUrl);
    paymentUrlBuild.searchParams.append('payment_notify_url', window.location.origin + this.url);
    const paymentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(paymentUrlBuild.href);
    // Stripe doesnt support showing in iframe
    if (payment.stripePaymentUrl) {
      window.location.replace(this.payment.stripePaymentUrl);
    } else {
      const dialogRef = this.dialog.open(PaymentDialogComponent, {
        width: '650px',
        data: { paymentUrl },
      });

      const handleListen = (event: MessageEvent) => {
        if (event.origin === window.origin && event.data === dialogMessage.close) {
          dialogRef.close();
        }
      };

      window?.addEventListener('message', handleListen.bind(this));

      //After the iframe is closed
      dialogRef
        ?.afterClosed()
        .pipe(switchMap(() => this.feedback.feedbackDialog(feedbackTypes.payment)))
        .subscribe(() => {
          window?.location.reload();
        });
    }
    const gtmTag = {
      event: 'page',
      page: this.router.url,
      type: (event.target as HTMLButtonElement).innerText,
      stage: 'one payment',
    };
    this.tagManagerService.pushTag(gtmTag);
  }

  getBackground() {
    let firstColour = 'var(--primary-color)';
    if (this.theme?.theme_colours?.primary) {
      firstColour = this.theme.theme_colours.primary;
    }
    return `${firstColour}`;
  }

  getFontColour(): string {
    if (this.theme?.theme_colours?.primary) {
      return calculateFontColour(this.theme?.theme_colours?.primary);
    }
    return '#FFF';
  }

  hasTheme(): boolean {
    const hasTheme = !!this.theme?.theme_colours?.primary && !!this.theme?.theme_colours?.accent;
    if (hasTheme) {
      this.display.changeTheme(this.theme.theme_colours.primary, this.theme.theme_colours.accent);
    }
    return hasTheme;
  }

  selectPayment(payment: Payment) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        type: null,
        hash: payment.hash,
      },
      // preserve the existing query params in the route
      queryParamsHandling: 'merge',
      // do not trigger navigation
      skipLocationChange: true,
    });
  }

  backPayment() {
    this.payment = null;
    this.payAllInvoices = false;
    this.peachpayEmbedded = false;
  }

  // Opens the next screen to select the payment option
  makePayment(event: Event) {
    const gtmTag = {
      event: 'page',
      page: this.router.url,
      type: (event.target as HTMLButtonElement).innerText,
      stage: 'all payment',
    };
    this.tagManagerService.pushTag(gtmTag);
    this.payAllInvoices = true;
  }

  treatingDoctors() {
    const treatingDoctors = this.payment.invoices
      .map((invoice) => invoice.treating_doc)
      .filter((doc, index, self) => doc && self.indexOf(doc) === index);
    return treatingDoctors.join(', ');
  }

  getPatient() {
    this.invoices =
      this.payment?.invoices?.map((inv) => {
        const patient = this.payment?.patients?.find((p) => p.patient_dbid === inv.patient_dbid);
        return {
          ...inv,
          fullName: [patient?.patient_name, patient?.patient_surname].filter(Boolean).join(' '),
        };
      }) || [];
  }

  onDownload(payment: Payment) {
    downloadSummaryOfPayments(payment, this.translateService);
  }

  activePayment() {
    return this.payment?.clientStatus === 'active';
  }

  onPaymentCompleted(status: 'completed' | 'cancelled' | 'expired' | 'error' | '') {
    if (status === 'completed') {
      this.paymentService
        .sendProofOfPayment$(this.payment.hash)
        .pipe(
          catchError(() => {
            this.alertService.snackbar(this.translateService.instant('proof_of_payment_failed'));
            return EMPTY;
          }),
        )
        .subscribe(() => {
          this.alertService.snackbar(this.translateService.instant('proof_of_payment_sent'));
        });
    }
  }
}

@Component({
  selector: 'gc-payment-dialog',
  templateUrl: 'payment-dialog.component.html',
  styleUrls: ['./payment-dialog.component.scss'],
  standalone: false,
})
export class PaymentDialogComponent {
  result = false;
  private readonly tagManagerService = inject(TagManagerService);
  private readonly router = inject(Router);
  constructor(
    public dialogRef: MatDialogRef<PaymentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Payment,
    private readonly feedback: FeedbackService,
  ) {}

  onNoClick(): void {
    const gtmTag = {
      event: 'page',
      page: this.router.url,
      type: 'payment-iframe',
      stage: 'cancel',
    };
    this.tagManagerService.pushTag(gtmTag);
    this.dialogRef.close();
  }

  onFeedbackClick(): void {
    const gtmTag = {
      event: 'page',
      page: this.router.url,
      type: 'payment-iframe',
      stage: 'feedback',
    };
    this.tagManagerService.pushTag(gtmTag);
    this.feedback.feedbackDialog(feedbackTypes.payment).subscribe();
  }
}
